import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DollarSign, CheckCircle, XCircle, RefreshCw, TrendingUp, Zap } from "lucide-react";

interface PaymentAnalyticsData {
  summary: {
    totalSucceeded: number;
    totalFailed: number;
    totalRecovered: number;
    totalRevenue: number;
    successRate: number;
    avgAttempts: number;
    firstAttemptSuccessRate: number;
  };
  dailyBreakdown: Array<{
    date: string;
    succeeded: number;
    failed: number;
    recovered: number;
    revenue: number;
  }>;
  recentEvents: Array<{
    id: string;
    eventType: string;
    amount: number;
    currency: string;
    attemptCount: number;
    recovered: boolean;
    failureReason: string | null;
    subscriptionPlan: string | null;
    createdAt: string;
    userName: string;
    email: string;
  }>;
  period: { days: number; since: string };
}

export default function AdminPaymentAnalytics() {
  const [days, setDays] = useState("30");

  const { data, isLoading } = useQuery<PaymentAnalyticsData>({
    queryKey: ["/api/admin/payment-analytics", days],
    queryFn: async () => {
      const res = await fetch(`/api/admin/payment-analytics?days=${days}`, { credentials: "include" });
      return res.json();
    },
  });

  const s = data?.summary;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Payment Analytics</h1>
          <p className="text-muted-foreground text-sm">Payment success rates, recovery tracking, and revenue</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : s ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Succeeded
                </div>
                <p className="text-2xl font-bold">{s.totalSucceeded}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <XCircle className="w-4 h-4 text-red-500" />
                  Failed
                </div>
                <p className="text-2xl font-bold">{s.totalFailed}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                  Success Rate
                </div>
                <p className="text-2xl font-bold">{s.successRate}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <RefreshCw className="w-4 h-4 text-amber-500" />
                  Recovered
                </div>
                <p className="text-2xl font-bold">{s.totalRecovered}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Zap className="w-4 h-4 text-purple-500" />
                  1st Attempt Rate
                </div>
                <p className="text-2xl font-bold">{s.firstAttemptSuccessRate}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  Revenue
                </div>
                <p className="text-2xl font-bold">${(s.totalRevenue / 100).toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>

          {/* Daily Breakdown */}
          {data.dailyBreakdown.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Daily Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left py-2 pr-4">Date</th>
                        <th className="text-right py-2 px-3">Succeeded</th>
                        <th className="text-right py-2 px-3">Failed</th>
                        <th className="text-right py-2 px-3">Recovered</th>
                        <th className="text-right py-2 px-3">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.dailyBreakdown.map((day) => (
                        <tr key={day.date} className="border-b last:border-0">
                          <td className="py-2 pr-4 font-medium">{new Date(day.date).toLocaleDateString("en-US", { timeZone: "America/Jamaica", month: "short", day: "numeric" })}</td>
                          <td className="text-right py-2 px-3 text-green-600">{day.succeeded}</td>
                          <td className="text-right py-2 px-3 text-red-600">{day.failed || "-"}</td>
                          <td className="text-right py-2 px-3 text-amber-600">{day.recovered || "-"}</td>
                          <td className="text-right py-2 px-3">${(Number(day.revenue) / 100).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Events */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Payment Events</CardTitle>
            </CardHeader>
            <CardContent>
              {data.recentEvents.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">No payment events recorded yet. Events will appear here as payments are processed.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left py-2 pr-3">Time</th>
                        <th className="text-left py-2 px-3">Student</th>
                        <th className="text-left py-2 px-3">Status</th>
                        <th className="text-right py-2 px-3">Amount</th>
                        <th className="text-center py-2 px-3">Attempt</th>
                        <th className="text-left py-2 px-3">Plan</th>
                        <th className="text-left py-2 pl-3">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentEvents.map((event) => (
                        <tr key={event.id} className="border-b last:border-0">
                          <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">
                            {new Date(event.createdAt).toLocaleDateString("en-US", { timeZone: "America/Jamaica", month: "short", day: "numeric" })}{" "}
                            {new Date(event.createdAt).toLocaleTimeString("en-US", { timeZone: "America/Jamaica", hour: "numeric", minute: "2-digit", hour12: true })}
                          </td>
                          <td className="py-2 px-3">
                            <div className="font-medium">{event.userName}</div>
                            <div className="text-xs text-muted-foreground">{event.email}</div>
                          </td>
                          <td className="py-2 px-3">
                            {event.eventType === "payment_succeeded" ? (
                              <Badge className="bg-green-500/10 text-green-700 border-green-500/20">
                                {event.recovered ? "Recovered" : "Success"}
                              </Badge>
                            ) : (
                              <Badge className="bg-red-500/10 text-red-700 border-red-500/20">Failed</Badge>
                            )}
                          </td>
                          <td className="text-right py-2 px-3 font-medium">
                            ${(event.amount / 100).toFixed(2)}
                          </td>
                          <td className="text-center py-2 px-3">
                            <span className={event.attemptCount > 1 ? "text-amber-600 font-medium" : ""}>
                              {event.attemptCount}
                            </span>
                          </td>
                          <td className="py-2 px-3 capitalize">{event.subscriptionPlan || "-"}</td>
                          <td className="py-2 pl-3 text-muted-foreground">
                            {event.failureReason || (event.recovered ? `Recovered after ${event.attemptCount} attempts` : "")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <p className="text-muted-foreground text-center py-12">No data available</p>
      )}
    </div>
  );
}
